import React, { useState, useRef, useLayoutEffect, useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  Box,
  List,
  ListItem,
  TextField,
  CircularProgress,
  Checkbox,
  Tooltip,
  IconButton,
  Paper,
  Typography,
  alpha,
  Button,
  useTheme as useMuiTheme,
} from '@mui/material';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../stores/store';
import { useListMessagesOnThread } from '../hooks/useListMessagesOnThread';
import { useFetchUser } from '../hooks/useFetchUsers';
import ReactMarkdown from 'react-markdown';
import chatBotImage from '../images/ChatBot.webp';
import { AssistantService } from '../api/AssistantService';
import LanguageIcon from '@mui/icons-material/Language';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Draggable from 'react-draggable';
import { useChatWindowStore } from '../stores/chatWindowStore';
import { useKeyBoardEventStore } from '../stores/keyEventStore';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useTheme } from '../contexts/ThemeContext';
import { keyframes } from '@mui/system';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { RequestFlag } from '../api/protosCompiled/inference/inference_pb';
import { useChatMessagesStore } from '../stores/chatMessagesStore';
import { Inference } from '../hooks/inferenceHooks';

// Enum for the tool types
type ToolType = 'web' | null;

const customPrompt = (estimateName: string) => {
  return [
    {
      id: 1,
      name: 'Use Web',
      description: `
                    System: You have access to web search capabilities.
                    When providing information:
                    1. Use the Internet Research Agent to verify and supplement your knowledge
                    2. Cite sources when possible
                    3. Clearly distinguish between your general knowledge and web-sourced information
                    4. Prioritize recent and authoritative sources
                    5. If web search fails or is unavailable, acknowledge this limitation
                    `,
    },
  ];
};

// Create thinking animation
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

// Add loading border animation
const loadingBorderAnimation = keyframes`
  0% {
    background-position: 0% 0%;
  }
  25% {
    background-position: 100% 0%;
  }
  50% {
    background-position: 100% 100%;
  }
  75% {
    background-position: 0% 100%;
  }
  100% {
    background-position: 0% 0%;
  }
`;

// Add glow pulse animation
const glowPulse = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(80, 150, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 15px rgba(80, 150, 255, 0.6);
  }
  100% {
    box-shadow: 0 0 5px rgba(80, 150, 255, 0.3);
  }
`;

// New component for "More coming..." indicator
function MoreComingIndicator({ active }: { active: boolean }) {
  if (!active) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        opacity: 0.7,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: theme => alpha(theme.palette.text.primary, 0.6),
          fontStyle: 'italic',
          fontSize: '0.75rem',
          borderRadius: '12px',
          py: 0.5,
          px: 1,
          backgroundColor: theme => alpha(theme.palette.background.paper, 0.4),
        }}
      >
        <AutorenewIcon
          sx={{
            fontSize: 14,
            mr: 0.5,
            animation: `${spin} 1.5s linear infinite`,
            color: theme => theme.palette.primary.main,
          }}
        />
        Processing...
      </Box>
    </Box>
  );
}

// Replace Ellipsis Hook with ThinkingBot Component
function ThinkingBotIndicator({ active }: { active: boolean }) {
  if (!active) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{ position: 'relative', width: 50, height: 50 }}>
        <Box
          sx={{
            position: 'absolute',
            top: -15,
            left: '50%',
            transform: 'translateX(-50%)',
            animation: `${float} 2s ease-in-out infinite`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2,
          }}
        >
          <AutorenewIcon
            sx={{
              fontSize: 20,
              color: theme => theme.palette.primary.main,
              animation: `${spin} 1.5s linear infinite`,
            }}
          />
        </Box>
        <Box
          component="img"
          src={chatBotImage}
          alt="Chat bot thinking"
          sx={{
            width: 40,
            height: 40,
            objectFit: 'contain',
            borderRadius: '50%',
          }}
        />
      </Box>
      <Typography
        variant="body2"
        sx={{
          ml: 1,
          fontStyle: 'italic',
          color: theme => alpha(theme.palette.text.primary, 0.7),
        }}
      >
        Thinking...
      </Typography>
    </Box>
  );
}

// Health Status Message Component
function HealthStatusMessage() {
  const { darkMode } = useTheme();
  const muiTheme = useMuiTheme();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box
        component="img"
        src={chatBotImage}
        alt="Chat bot"
        sx={{
          width: 40,
          height: 40,
          objectFit: 'contain',
          borderRadius: '50%',
          mr: 2,
        }}
      />
      <Paper
        sx={{
          backgroundColor: darkMode
            ? alpha(muiTheme.palette.warning.dark, 0.2)
            : alpha(muiTheme.palette.warning.light, 0.15),
          border: `1px solid ${alpha(muiTheme.palette.warning.main, 0.3)}`,
          p: 2,
          borderRadius: 2,
          maxWidth: '80%',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme => theme.palette.warning.main,
            fontWeight: 500,
          }}
        >
          Chat Service is down, please wait while we start it up
        </Typography>
      </Paper>
    </Box>
  );
}

type Who = 'user' | 'assistant';

export function ChatWindow() {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();
  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const chatWindowPosition = useChatWindowStore(state => state.position);
  const showChatWindow = useStore(useKeyBoardEventStore).showChatWindow;
  const setShowChatWindow = useStore(useKeyBoardEventStore).setShowChatWindow;

  // Use the persistent chat messages store
  const chatMessagesStore = useChatMessagesStore();
  const messages = chatMessagesStore.getMessages(selectedProject.id);

  const [userInput, setUserInput] = useState('');
  const [awaitingResponse, setAwaitingResponse] = useState(false);
  const [selectedTool, setSelectedTool] = useState<ToolType>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isReceivingMessage, setIsReceivingMessage] = useState(false);
  const [userScrolledToBottom, setUserScrolledToBottom] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);

  const { data: messageData, isLoading } = useListMessagesOnThread(selectedProject.id);
  const { data: user } = useFetchUser();
  const { data: healthCheckData, error: healthCheckError } = Inference.useHealthCheck();

  useEffect(() => {
    console.log('healthCheckData', healthCheckData);
  }, [healthCheckData]);

  // Check if the service is unhealthy
  const isServiceUnhealthy = useMemo(() => {
    if (healthCheckError) return true;
    if (healthCheckData?.message?.includes('UNHEALTHY')) return true;
    return false;
  }, [healthCheckData, healthCheckError]);

  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, []);

  useLayoutEffect(() => {
    if (!isReceivingMessage && userScrolledToBottom) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom, isReceivingMessage, userScrolledToBottom]);

  useEffect(() => {
    const container = messageContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isAtBottom = container.scrollHeight - container.clientHeight - container.scrollTop < 30;

      setUserScrolledToBottom(isAtBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize messages from API when available
  useEffect(() => {
    if (!messageData || !selectedProject.id) return;

    // Only initialize if not already initialized for this project
    if (!chatMessagesStore.isInitialized(selectedProject.id)) {
      chatMessagesStore.initializeMessages(
        selectedProject.id,
        messageData,
        user?.user?.userphotourl
      );
    }
  }, [selectedProject.id, messageData, user, chatMessagesStore]);

  // Helper function to append messages using the store
  const appendMessage = useCallback(
    (who: Who, text: string, imageUrl?: string, forceNew: boolean = false) => {
      if (forceNew || text.includes('"batch_analysis"')) {
        // Always create a new message
        chatMessagesStore.addMessage(selectedProject.id, { who, text, imageUrl });
      } else {
        // For streaming, check if we can update the last message
        const currentMessages = chatMessagesStore.getMessages(selectedProject.id);
        if (currentMessages.length > 0 && currentMessages[currentMessages.length - 1].who === who) {
          // Update the last message
          chatMessagesStore.updateLastMessage(selectedProject.id, text);
        } else {
          // Add new message
          chatMessagesStore.addMessage(selectedProject.id, { who, text, imageUrl });
        }
      }
    },
    [selectedProject.id, chatMessagesStore]
  );

  async function sendMessage(input: string, promptIndex: number = 0) {
    // Only add the prompt if a tool is selected
    if (selectedTool === 'web' && promptIndex === 0) {
      input = `${customPrompt(selectedEstimate?.name || '')[0].description}\n\n${input}`;
    }

    if (input.trim() !== '') {
      appendMessage('user', input.trim(), user?.user?.userphotourl);
      setUserInput('');
      setAwaitingResponse(true);
      setUserScrolledToBottom(true);

      try {
        let firstChunk = true;

        await AssistantService.streamMessage({
          usermessage: input.trim(),
          projectid: selectedProject.id,
          estimateid: selectedEstimate?.estimateid || '',
          requestflag:
            selectedTool === 'web'
              ? RequestFlag.REQUEST_FLAG_WEB_SEARCH
              : RequestFlag.REQUEST_FLAG_UNSPECIFIED,
          onMessage: (chunk: string) => {
            if (firstChunk) {
              appendMessage('assistant', chunk, chatBotImage);
              setAwaitingResponse(false);
              setIsReceivingMessage(true);
              firstChunk = false;
            } else {
              appendMessage('assistant', chunk, chatBotImage);
            }
          },
        });

        // Start fade-out animation
        setIsFadingOut(true);

        // After fade animation completes, reset states
        setTimeout(() => {
          setIsReceivingMessage(false);
          setTimeout(() => {
            setIsFadingOut(false);
          }, 100);
        }, 3000);
      } catch (error) {
        console.error('Error sending message:', error);
        setAwaitingResponse(false);
        setIsReceivingMessage(false);
        setIsFadingOut(false);
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (userInput.trim() !== '') {
        sendMessage(userInput.trim());
      }
    }
  };

  const markdownComponents = useMemo(
    () => ({
      a: ({ node, ...props }: any) => (
        <a {...props} target="_blank" rel="noopener noreferrer">
          {props.children}
        </a>
      ),
    }),
    []
  );

  // New method to copy formatted text to clipboard
  const copyFormattedText = async (text: string, element: HTMLElement | null = null) => {
    try {
      if (element) {
        // Copy directly from the rendered element (with formatting)
        const selection = window.getSelection();
        const range = document.createRange();

        // Clear existing selection
        selection?.removeAllRanges();

        // Select the rendered markdown content
        range.selectNodeContents(element);
        selection?.addRange(range);

        // Copy the selected content
        document.execCommand('copy');

        // Clear selection
        selection?.removeAllRanges();

        return true;
      } else {
        // Fallback to copying markdown directly
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      console.error('Failed to copy formatted text:', err);
      return false;
    }
  };

  // Modified handleCopyMessage to use DOM selection for copying
  const handleCopyMessage = useCallback((text: string, index: number) => {
    // Find the message element by index
    const messageElement = document.querySelector(
      `[data-message-index="${index}"] .message-content`
    );

    copyFormattedText(text, messageElement as HTMLElement)
      .then(success => {
        if (success) {
          setCopiedIndex(index);
          setTimeout(() => setCopiedIndex(null), 2000);
        }
      })
      .catch(err => {
        console.error('Failed to copy message:', err);
      });
  }, []);

  const renderedMessages = useMemo(() => {
    return messages.map((msg, index) => {
      let displayText = msg.text;
      if (msg.who === 'user') {
        customPrompt(selectedEstimate?.name || '').forEach(prompt => {
          const promptText = prompt.description.trim();
          if (displayText.startsWith(promptText)) {
            displayText = displayText.slice(promptText.length).trimStart();
          }
        });
      }

      // Check if this is the last assistant message while receiving data or fading out
      const isLastAssistantMessage =
        (isReceivingMessage || isFadingOut) &&
        msg.who === 'assistant' &&
        index === messages.length - 1;

      // Themed message bubbles using reactive theme
      return (
        <ListItem
          key={index}
          data-message-index={index}
          sx={{
            py: 1,
            px: 2,
            position: 'relative',
          }}
          aria-label={`${msg.who === 'user' ? 'Your message' : 'Assistant message'}`}
        >
          <Paper
            sx={{
              backgroundColor:
                msg.who === 'user'
                  ? darkMode
                    ? alpha(muiTheme.palette.primary.dark, 0.2)
                    : alpha(muiTheme.palette.primary.light, 0.15)
                  : darkMode
                    ? alpha(muiTheme.palette.background.paper, 0.5)
                    : alpha(muiTheme.palette.background.paper, 0.7),
              p: 2,
              borderRadius: 2,
              maxWidth: '80%',
              minHeight: '48px',
              wordWrap: 'break-word',
              marginLeft: msg.who === 'user' ? 'auto' : 0,
              boxShadow: muiTheme.shadows[1],
              border: `1px solid ${
                darkMode
                  ? alpha(muiTheme.palette.divider, 0.1)
                  : alpha(muiTheme.palette.divider, 0.2)
              }`,
              color: muiTheme.palette.text.primary,
              position: 'relative',
              ...(isLastAssistantMessage && {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -3,
                  right: -3,
                  bottom: -3,
                  left: -3,
                  zIndex: -1,
                  borderRadius: '12px',
                  background: `linear-gradient(90deg, 
                    ${muiTheme.palette.primary.main}, 
                    ${muiTheme.palette.secondary.main || alpha(muiTheme.palette.primary.light, 0.6)}, 
                    ${muiTheme.palette.primary.main})`,
                  backgroundSize: '300% 300%',
                  animation: `${loadingBorderAnimation} 3s linear infinite`,
                  boxShadow: `0 0 10px ${alpha(muiTheme.palette.primary.main, 0.5)}`,
                  animationTimingFunction: 'ease-in-out',
                  opacity: isFadingOut ? 0 : 1,
                  transition: 'opacity 3s ease-out',
                },
                animation: isFadingOut ? 'none' : `${glowPulse} 2s infinite alternate`,
                transition: 'all 0.8s ease-out',
              }),
              '& a': {
                color: muiTheme.palette.primary.main,
                textDecoration: 'underline',
                '&:hover': {
                  color: muiTheme.palette.primary.dark,
                },
              },
              '& p': {
                margin: '0.5em 0',
              },
              '& p:first-of-type': {
                marginTop: 0,
              },
              '& p:last-of-type': {
                marginBottom: 0,
              },
            }}
            elevation={1}
          >
            {msg.who === 'assistant' && (
              <Tooltip
                title={copiedIndex === index ? 'Copied!' : 'Copy to clipboard'}
                arrow
                placement="top"
                PopperProps={{
                  disablePortal: false,
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      enabled: true,
                      options: {
                        altAxis: true,
                        altBoundary: true,
                        rootBoundary: 'document',
                      },
                    },
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ],
                  style: { zIndex: 10001 },
                }}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: darkMode ? 'primary.dark' : 'primary.main',
                      color: 'primary.contrastText',
                      fontSize: '0.75rem',
                      fontWeight: 'medium',
                      p: 1,
                      boxShadow: 2,
                      '& .MuiTooltip-arrow': {
                        color: darkMode ? 'primary.dark' : 'primary.main',
                      },
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    zIndex: 10,
                    color: 'red',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleCopyMessage(displayText, index)}
                    sx={{
                      backgroundColor: darkMode
                        ? alpha(muiTheme.palette.background.default, 0.8)
                        : alpha(muiTheme.palette.background.paper, 0.9),
                      color:
                        copiedIndex === index
                          ? muiTheme.palette.primary.main
                          : darkMode
                            ? alpha(muiTheme.palette.common.white, 0.9)
                            : alpha(muiTheme.palette.common.black, 0.8),
                      padding: '5px',
                      border: `1px solid ${
                        darkMode
                          ? alpha(muiTheme.palette.primary.main, 0.3)
                          : alpha(muiTheme.palette.primary.main, 0.2)
                      }`,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      '&:hover': {
                        backgroundColor: darkMode
                          ? alpha(muiTheme.palette.primary.dark, 0.2)
                          : alpha(muiTheme.palette.primary.light, 0.2),
                        color:
                          copiedIndex === index
                            ? muiTheme.palette.primary.dark
                            : muiTheme.palette.text.primary,
                      },
                      transition: 'all 0.2s ease',
                    }}
                    aria-label="Copy message"
                  >
                    <ContentCopyIcon fontSize="small" sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
            <div className="message-content">
              <ReactMarkdown components={markdownComponents}>{displayText}</ReactMarkdown>
            </div>
          </Paper>
        </ListItem>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    messages,
    markdownComponents,
    muiTheme,
    darkMode,
    copiedIndex,
    handleCopyMessage,
    isReceivingMessage,
    isFadingOut,
  ]);

  const handleClearUserInput = () => {
    if (userInput.toLowerCase() === 'c') {
      setUserInput('');
    }
  };

  const handleToolToggle = (tool: ToolType) => {
    setSelectedTool(prevTool => (prevTool === tool ? null : tool));
  };

  // Add cleanup effect when chat window is closed
  useEffect(() => {
    if (!showChatWindow) {
      // Only reset UI states, preserve messages
      setAwaitingResponse(false);
      setIsReceivingMessage(false);
      setIsFadingOut(false);
      setSelectedTool(null);
      // Do NOT reset messages here to preserve chat history
    }
  }, [showChatWindow]);

  return ReactDOM.createPortal(
    <Draggable handle=".chat-drag-handle" bounds="parent">
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          left: chatWindowPosition.left,
          bottom: chatWindowPosition.bottom,
          zIndex: 10000,
          width: '500px',
          height: '75vh',
          resize: 'horizontal',
          overflow: 'hidden',
          display: showChatWindow ? 'flex' : 'none',
          flexDirection: 'column',
          backgroundColor: darkMode
            ? alpha(muiTheme.palette.background.paper, 0.9)
            : muiTheme.palette.background.paper,
          borderRadius: 1,
          pointerEvents: 'auto',
          border: `1px solid ${muiTheme.palette.divider}`,
          transition: 'all 0.3s ease',
        }}
      >
        <Box
          className="chat-drag-handle"
          sx={{
            cursor: 'move',
            p: 1.5,
            backgroundColor: darkMode
              ? muiTheme.palette.primary.dark
              : muiTheme.palette.primary.main,
            color: muiTheme.palette.primary.contrastText,
            fontWeight: 500,
            userSelect: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${alpha(muiTheme.palette.primary.dark, 0.2)}`,
          }}
        >
          <Typography variant="subtitle1" component="h2">
            Chat Assistant
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowChatWindow(false)}
            sx={{
              color: muiTheme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: alpha(muiTheme.palette.common.white, 0.15),
              },
            }}
            aria-label="Close chat window"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          ref={messageContainerRef}
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            p: 2,
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.background.default, 0.4)
              : alpha(muiTheme.palette.background.default, 0.7),
            paddingBottom: 4,
          }}
        >
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress color="primary" size={40} thickness={4} />
            </Box>
          ) : (
            <>
              <List sx={{ p: 0 }}>{renderedMessages}</List>
              {isServiceUnhealthy && (
                <Box sx={{ pl: 2, pr: 2, display: 'flex', justifyContent: 'flex-start' }}>
                  <HealthStatusMessage />
                </Box>
              )}
              {awaitingResponse && !isServiceUnhealthy && (
                <Box sx={{ pl: 2, pr: 2, display: 'flex', justifyContent: 'flex-start' }}>
                  <ThinkingBotIndicator active={awaitingResponse} />
                </Box>
              )}
              {(isReceivingMessage || isFadingOut) &&
                !awaitingResponse &&
                !isServiceUnhealthy &&
                messages.length > 0 && (
                  <Box
                    sx={{
                      pl: 2,
                      pr: 2,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      opacity: isFadingOut ? 0 : 1,
                      transition: 'opacity 3s ease-out',
                    }}
                  >
                    <MoreComingIndicator active={isReceivingMessage} />
                  </Box>
                )}
            </>
          )}
          <div ref={messagesEndRef} style={{ height: '20px', marginTop: '8px' }} />
        </Box>

        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            p: 2,
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.background.paper, 0.7)
              : muiTheme.palette.background.paper,
          }}
        >
          <TextField
            ref={inputRef}
            autoFocus
            multiline
            rows={3}
            onFocus={handleClearUserInput}
            placeholder={isServiceUnhealthy ? 'Service is starting up...' : 'Type a message...'}
            value={userInput}
            onChange={e => setUserInput(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              e.stopPropagation();
              handleKeyDown(e);
            }}
            variant="outlined"
            fullWidth
            disabled={isServiceUnhealthy}
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.background.paper, 0.8)
                  : alpha(muiTheme.palette.background.paper, 0.9),
                borderRadius: 1,
                border: `1px solid ${muiTheme.palette.divider}`,
                opacity: isServiceUnhealthy ? 0.6 : 1,
              },
              '& .MuiInputBase-input': {
                color: muiTheme.palette.text.primary,
              },
            }}
            InputProps={{
              sx: {
                p: 1.5,
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1,
            }}
          >
            <Box display="flex" alignItems="center">
              <Tooltip title="Use web search capabilities" arrow>
                <Box>
                  <Checkbox
                    icon={<LanguageIcon color="action" />}
                    checkedIcon={<LanguageIcon color="primary" />}
                    checked={selectedTool === 'web'}
                    onChange={() => handleToolToggle('web')}
                    aria-label={selectedTool === 'web' ? 'Disable web search' : 'Enable web search'}
                    sx={{
                      p: 1,
                      '& .MuiSvgIcon-root': {
                        fontSize: 24,
                      },
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>

            <Button
              variant="contained"
              color="primary"
              disabled={!userInput.trim() || isServiceUnhealthy}
              onClick={() => sendMessage(userInput)}
              endIcon={<SendIcon />}
              aria-label="Send message"
              sx={{
                borderRadius: 6,
                px: 2,
                py: 1,
                opacity: isServiceUnhealthy ? 0.6 : 1,
              }}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Paper>
    </Draggable>,
    document.body
  );
}

export default ChatWindow;
